import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { useWaitForCeleryTaskResultsAsync } from '@/hooks/useWaitForCeleryTaskResultsAsync'
import { usePingDevice } from '@/queries/deviceQueries'
import { showToast } from '@/theme/notifications'

type UseDevicePingParams = {
  deviceId?: string
  onSuccess?: () => void
}

export const useDevicePing = ({ deviceId, onSuccess }: UseDevicePingParams) => {
  const intl = useIntl()

  const { mutateAsync: pingDevice, isPending: isPingingDevice } =
    usePingDevice()

  const { waitForCeleryTaskResultsAsync, isPending: isPolling } =
    useWaitForCeleryTaskResultsAsync()

  const handlePing = useCallback(async () => {
    if (!deviceId) {
      return
    }

    try {
      const { task_id } = await pingDevice(deviceId)
      await waitForCeleryTaskResultsAsync(task_id)

      onSuccess?.()
      showToast(
        intl.formatMessage({ id: 'deployments.details.device.ping.success' }),
        'success'
      )
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'deployments.details.device.ping.error' })
        ),
        'error'
      )
    }
  }, [pingDevice, waitForCeleryTaskResultsAsync, deviceId, onSuccess, intl])

  return useMemo(
    () => ({ isPinging: isPingingDevice || isPolling, pingDevice: handlePing }),
    [isPingingDevice, isPolling, handlePing]
  )
}
